import { useEffect, useState } from 'react';
import type { MutableRefObject } from 'react';
import Handsontable from 'handsontable/base';
import type { CopyPaste } from 'handsontable/plugins/copyPaste/copyPaste';
import { getDefaultTableColWidth } from './utils/hotInstanceUtils/grid/hotTableGridDefaultDimensionUtils';
import { getCellValueArray } from './utils/dataUtils/persistence/tablePersistenceValueUtils';
import { convertColWidthsGUtoPx } from './utils/dataUtils/columnSize/tableColWidthConversionUtils';
import { getColWidthsInList } from './utils/hotInstanceUtils/columnSize/hotTableColWidthInListUtils';
import { applyRawTableContentToHotInstance } from './utils/hotInstanceUtils/persistence/hotTableRehydrationUtils';
import { removeHyperFormulaUndoRedoEntriesForSheet } from './utils/hotInstanceUtils/undoRedo/hotTableUndoRedoHyperFormulaUtils';
import { sanitizeClipboardHtmlForPaste } from './utils/dataUtils/clipboard/tableClipboardUtils';
import { TableAxis } from '../../../common/table/TableTypes';
import type { TableContentData } from '../../../common/table/TableTypes';
import type { ImMNElement } from '../../../common/elements/elementModelTypes';
import { MILANOTE_EDITING_PLUGIN_NAME } from './modules/MilanoteEditingPlugin';
import { MILANOTE_FORMULA_AUTOCOMPLETE_PLUGIN_NAME } from './modules/MilanoteFormulaAutocompletePlugin';
import { MILANOTE_FORMULA_CELL_REFERENCE_PLUGIN_NAME } from './modules/MilanoteFormulaCellReferencePlugin';
import { LEGACY_MILANOTE_FORMULA_CELL_REFERENCE_PLUGIN_NAME } from './legacy/modules/MilanoteFormulaCellReferencePlugin';
import { LEGACY_MILANOTE_FORMULA_AUTOCOMPLETE_PLUGIN_NAME } from './legacy/modules/MilanoteFormulaAutocompletePlugin';
import { LEGACY_MILANOTE_EDITING_PLUGIN_NAME } from './legacy/modules/MilanoteEditingPlugin';
import { MILANOTE_EDITING_CLEANUP_PLUGIN_NAME } from './modules/MilanoteEditingCleanupPlugin';
import { LEGACY_MILANOTE_EDITING_CLEANUP_PLUGIN_NAME } from './legacy/modules/MilanoteEditingCleanupPlugin';
import { LEGACY_MILANOTE_CONTEXT_MENU_PLUGIN_NAME } from './legacy/modules/MilanoteContextMenuPlugin';
import { MILANOTE_CONTEXT_MENU_PLUGIN_NAME } from './modules/MilanoteContextMenuPlugin';
import hyperFormulaInstance from './manager/hyperFormulaInstance';
import getClientConfig from '../../utils/getClientConfig';
import { getElementId } from '../../../common/elements/utils/elementPropertyUtils';
import type { GridSettings } from 'handsontable/settings';
import type { UseTableCellEditingHandlersReturn } from './handlers/useTableCellEditingHandlers';
import type { UseTableSelectionHandlersReturn } from './handlers/useTableSelectionHandlers';
import type { UseTableMoveColumnRowHandlersReturn } from './handlers/useTableMoveColumnRowHandlers';
import type { UseTableContextMenuReturn } from './handlers/useTableContextMenu';
import type { MilanoteHotTableInstance } from './tableHotInstanceTypes';
import type { MilanoteEditorRef } from '../../components/editor/milanoteEditor/MilanoteEditorTypes';
import { isNumber } from 'lodash';

const config = getClientConfig();

type HandsontableHook<Key extends keyof GridSettings> = NonNullable<GridSettings[Key]>;

type HotTableRendererPortalManagerRef = {
    getRendererWrapper: () => HandsontableHook<'renderer'>;
    afterViewRender: HandsontableHook<'afterViewRender'>;
};

type MilanoteContextMenuPlugin = {
    removeHotContextMenuEventListeners: () => void;
};

// Handsontable doesn't seem to expose these types
type MilanoteCopyPasteFocusableElement = {
    mainElement: {
        value: string;
        select: () => void;
    };
    focus?: (this: MilanoteCopyPasteFocusableElement) => void;
};

type HotTableCopyPastePlugin = CopyPaste & {
    focusableElement?: MilanoteCopyPasteFocusableElement;
};

type UseInitializeHotTableArgs = {
    element: ImMNElement;
    elementId: string;
    tableContentData: TableContentData;
    tableContentColWidthsGU: number[];
    hotTableInstanceRef: MutableRefObject<Handsontable | null>;
    hotTableContainerRef: MutableRefObject<HTMLDivElement | null>;
    hotTableRendererPortalManagerRef: MutableRefObject<HotTableRendererPortalManagerRef | null>;
    gridSize: number;
    isDarkMode: boolean;
    inList?: string;
    isReadOnly: boolean;
    isResizing: boolean;
    getContextZoomScale: () => number;
    showTitle: boolean;
    showBottomPanel: boolean;
    locale: string;
    filterQuery?: string;
    startEditingCellEditor: (cellCoords: { row: number; col: number }) => void;
    finishEditingCellEditor: () => void;
    tableCellEditingHandlers: UseTableCellEditingHandlersReturn;
    tableCellSelectionHandlers: UseTableSelectionHandlersReturn;
    tableMoveColumnRowHandlers: UseTableMoveColumnRowHandlersReturn;
    tableContextMenuHandlers: UseTableContextMenuReturn;
    jumpToCaption: () => void;
    jumpToTitle: () => void;
    cellEditorRef: MutableRefObject<MilanoteEditorRef | null>;
    dropdownRef: MutableRefObject<unknown>;
};

/**
 * Creates the Handsontable config used for the table instance lifecycle.
 */
const createHotTableSettings = ({
    elementId,
    tableContentData,
    tableContentColWidthsGU,
    hotTableInstanceRef,
    hotTableContainerRef,
    hotTableRendererPortalManagerRef,
    gridSize,
    inList,
    isReadOnly,
    getContextZoomScale,
    tableCellEditingHandlers,
    tableCellSelectionHandlers,
    tableMoveColumnRowHandlers,
    tableContextMenuHandlers,
    jumpToCaption,
    jumpToTitle,
    cellEditorRef,
    dropdownRef,
}: UseInitializeHotTableArgs): GridSettings => {
    const resizeToWidth = getColWidthsInList(inList, hotTableContainerRef, getContextZoomScale);
    const tableContentColWidthsPx = convertColWidthsGUtoPx(tableContentColWidthsGU, gridSize, resizeToWidth);

    return {
        licenseKey: config.handsontable?.licenseKey,
        data: getCellValueArray(tableContentData),
        editor: isReadOnly ? undefined : 'milanoteCellEditor',
        renderer: hotTableRendererPortalManagerRef.current?.getRendererWrapper(),
        colHeaders: true,
        rowHeaders: true,
        autoColumnSize: false,
        autoRowSize: false,
        autoWrapRow: true,
        manualRowMove: true,
        manualColumnMove: true,
        columnHeaderHeight: 0,
        height: 'auto',
        // @ts-ignore I assume that the type here is incorrect? Sheet name has been there since Aug 2023 - 32ee04d7
        formulas: { engine: hyperFormulaInstance, sheetName: elementId },
        manualColumnResize: tableContentColWidthsPx,
        contextMenu: tableContextMenuHandlers.getTableContextMenuConfig,
        outsideClickDeselects: false,
        colWidths: () => getDefaultTableColWidth(gridSize),
        sanitizer: sanitizeClipboardHtmlForPaste,
        // NOTE: Needs to be in the function format to avoid the `this` context being lost
        afterInit(this: Handsontable) {
            // This is a hack to get the table to get handsontable context menu event only triggered when there are
            // cell selections
            const milanoteContextMenuPlugin = this.getPlugin(MILANOTE_CONTEXT_MENU_PLUGIN_NAME) as unknown as
                | MilanoteContextMenuPlugin
                | undefined;
            milanoteContextMenuPlugin?.removeHotContextMenuEventListeners();

            // This is a hack to force handsontable to open keyboard on cell selection on the iPad.
            // Handsontable, by default, does not do this: https://github.com/handsontable/handsontable/pull/5529
            const copyPastePluginInstance = this.getPlugin('CopyPaste') as HotTableCopyPastePlugin | undefined;
            if (copyPastePluginInstance?.focusableElement?.focus && !isReadOnly) {
                copyPastePluginInstance.focusableElement.focus = function (this: MilanoteCopyPasteFocusableElement) {
                    this.mainElement.value = ' ';
                    this.mainElement.select();
                };
            }
        },
        afterViewRender: (...args) => hotTableRendererPortalManagerRef.current?.afterViewRender(...args),
        beforeChange: tableCellEditingHandlers.beforeChange,
        afterChange: tableCellEditingHandlers.afterChange,
        afterCreateRow: tableCellEditingHandlers.afterGridInsert(TableAxis.ROW),
        afterCreateCol: tableCellEditingHandlers.afterGridInsert(TableAxis.COL),
        afterRemoveRow: tableCellEditingHandlers.afterGridRemove(TableAxis.ROW),
        afterRemoveCol: tableCellEditingHandlers.afterGridRemove(TableAxis.COL),
        beforeAutofill: tableCellEditingHandlers.beforeAutofill,
        beforeCopy: tableCellEditingHandlers.beforeCopy,
        beforeCut: tableCellEditingHandlers.beforeCut,
        beforePaste: tableCellEditingHandlers.beforePaste,
        beforeOnCellMouseDown: (...args) => {
            tableCellEditingHandlers.beforeOnCellMouseDown(...args);
            tableMoveColumnRowHandlers.beforeOnCellMouseDown(...args);
        },
        beforeRowMove: tableCellEditingHandlers.beforeGridMovement(TableAxis.ROW),
        beforeColumnMove: tableCellEditingHandlers.beforeGridMovement(TableAxis.COL),
        afterRowMove: tableCellEditingHandlers.afterGridMovement(TableAxis.ROW),
        afterColumnMove: tableCellEditingHandlers.afterGridMovement(TableAxis.COL),
        afterSelection: tableCellSelectionHandlers.afterSelection,
        afterSelectionEnd: tableCellSelectionHandlers.afterSelectionEnd,
        afterDeselect: tableCellSelectionHandlers.afterDeselect,
        cellEditorRef,
        [MILANOTE_EDITING_PLUGIN_NAME]: {
            jumpToCaption,
            jumpToTitle,
            isReadOnly,
        },
        [MILANOTE_EDITING_CLEANUP_PLUGIN_NAME]: true,
        [MILANOTE_FORMULA_CELL_REFERENCE_PLUGIN_NAME]: {
            cellEditorRef,
        },
        [MILANOTE_FORMULA_AUTOCOMPLETE_PLUGIN_NAME]: {
            enabled: true,
            cellEditorRef,
            dropdownRef,
        },
        [MILANOTE_CONTEXT_MENU_PLUGIN_NAME]: true,
        [LEGACY_MILANOTE_FORMULA_CELL_REFERENCE_PLUGIN_NAME]: false,
        [LEGACY_MILANOTE_FORMULA_AUTOCOMPLETE_PLUGIN_NAME]: false,
        [LEGACY_MILANOTE_EDITING_PLUGIN_NAME]: false,
        [LEGACY_MILANOTE_EDITING_CLEANUP_PLUGIN_NAME]: false,
        [LEGACY_MILANOTE_CONTEXT_MENU_PLUGIN_NAME]: false,
    };
};

/**
 * Cleans up the hot instance while giving React time to finish unmounting dependants.
 */
const destroyHotTableInstance = (hotTableInstance: Handsontable.Core | null) => {
    // HOT CopyPaste plugin add paste event listeners on `document`.
    // When a late paste event occurs after Handsontable instance is destroyed,
    // this could cause an error as it will try to unsafely access `getActiveEditor()` from hotInstance.
    hotTableInstance?.getPlugin<'copyPaste'>('copyPaste')?.disablePlugin();

    setTimeout(() => {
        hotTableInstance?.destroy();
    });
};

/**
 * Initialises and tears down the Handsontable instance for the current table.
 */
const useInitializeHotTable = (args: UseInitializeHotTableArgs) => {
    const {
        element,
        tableContentData,
        tableContentColWidthsGU,
        hotTableInstanceRef,
        hotTableContainerRef,
        gridSize,
        isDarkMode,
        isResizing,
        getContextZoomScale,
        showTitle,
        showBottomPanel,
        locale,
        filterQuery,
        startEditingCellEditor,
        finishEditingCellEditor,
    } = args;
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        if (!hotTableContainerRef.current) return;

        const hotTableInstance = new Handsontable(
            hotTableContainerRef.current,
            createHotTableSettings(args),
        ) as MilanoteHotTableInstance;

        hotTableInstanceRef.current = hotTableInstance;

        // Set Milanote-specific runtime props to a newly created Handsontable instance
        hotTableInstance.milanoteProps = {
            gridSize,
            isDarkMode,
            showTitle,
            showBottomPanel,
            getContextZoomScale,
            hotTableContainerRef,
            isResizing,
            elementId: getElementId(element),
            locale,
            filterQuery,
            startEditingCellEditor,
            finishEditingCellEditor,
        };

        const initialTableContent = {
            data: tableContentData,
            colWidthsGU: tableContentColWidthsGU,
        };

        // We need to initialise the cell meta objects & formula plugins of the new handsontable instance
        applyRawTableContentToHotInstance(hotTableInstance, initialTableContent, gridSize, 'load');

        // HOT starts with an empty undo stack, so remove HyperFormula's mount-time sheet setup history
        const formulasPlugin = hotTableInstance.getPlugin<'formulas'>('formulas');
        // just checking if it's defined
        if (isNumber(formulasPlugin.sheetId)) {
            removeHyperFormulaUndoRedoEntriesForSheet(hotTableInstance, formulasPlugin.sheetId);
        }

        setMounted(true);

        return () => {
            destroyHotTableInstance(hotTableInstance);
        };
    }, []);

    /**
     * Force render after table has fully mounted. This will ensure that the row ref headers heights are correct.
     */
    useEffect(() => {
        if (!mounted) return;

        hotTableInstanceRef.current?.render();
    }, [mounted]);

    return mounted;
};

export default useInitializeHotTable;
